import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ban, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { UserAvatar } from "@/components/UserBits";
import { HonorAdminCard } from "@/components/admin/HonorAdminCard";

const ROWS = [
  ["missions", "Tagesmissionen", "Kein Bonus mehr für erledigte Tagesmissionen"],
  ["quest", "Community-Aufgaben", "Beiträge zählen nicht, keine Punkte, kein Helden-Abzeichen"],
  ["points", "Komplettes Punkte-System", "Erhält keinerlei Punkte mehr und kann nichts einlösen oder einsetzen"],
];

export default function MemberExclusions() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/admin/users/${id}/exclusions`).then(({ data }) => setM(data)).catch((e) => { toast.error(formatErr(e)); setM(false); }); }, [id]);
  const save = async (patch, msg) => {
    setBusy(true);
    try { const { data } = await api.put(`/admin/users/${id}/exclusions`, patch); setM((x) => ({ ...x, ...data })); toast.success(msg); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (m === null) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  if (!m) return <p className="text-center py-20 text-slate-400">Mitglied nicht gefunden</p>;
  const all = ROWS.every(([k]) => m[k]);
  const allOn = Object.fromEntries(ROWS.map(([k]) => [k, true]));
  const allOff = Object.fromEntries(ROWS.map(([k]) => [k, false]));
  return (
    <div className="max-w-xl mx-auto space-y-5" data-testid="member-exclusions-page">
      <button onClick={() => navigate(-1)} className="text-sm font-bold text-slate-500 inline-flex items-center gap-1" data-testid="member-exclusions-back"><ArrowLeft className="w-4 h-4" /> Zurück</button>
      <div className="rounded-3xl bg-white border border-pink-100 p-5 flex items-center gap-4">
        <UserAvatar userId={m.user_id} name={m.name} picture={m.picture} size="w-14 h-14" noStory />
        <div className="min-w-0">
          <h1 className="text-xl font-extrabold truncate" data-testid="member-exclusions-name">{m.name}</h1>
          <p className="text-xs text-slate-400 truncate">{m.email}</p>
          <p className="text-sm font-bold text-rose-600">{(m.balance || 0).toLocaleString("de-DE")} Punkte</p>
        </div>
      </div>
      <HonorAdminCard userId={m.user_id} name={m.name} />
      <div className="rounded-3xl bg-white border border-pink-100 p-5 space-y-4">
        <p className="font-extrabold">Ausschließen von …</p>
        {ROWS.map(([k, label, hint]) => (
          <label key={k} className="flex items-start justify-between gap-4" data-testid={`member-excl-row-${k}`}>
            <span><span className="block text-sm font-bold">{label}</span><span className="block text-xs text-slate-400">{hint}</span></span>
            <Switch checked={!!m[k]} disabled={busy} onCheckedChange={(v) => save({ [k]: v }, v ? `Von ${label} ausgeschlossen` : `${label} wieder zugelassen`)} data-testid={`member-excl-${k}`} />
          </label>
        ))}
        {all ? (
          <button onClick={() => save(allOff, "Alle Sperren aufgehoben")} disabled={busy} data-testid="member-excl-none" className="w-full h-12 rounded-full border-2 border-emerald-300 text-emerald-700 font-bold inline-flex items-center justify-center gap-2"><ShieldCheck className="w-4 h-4" /> Alle Sperren aufheben</button>
        ) : (
          <button onClick={() => window.confirm(`${m.name} von allem ausschließen? Das Mitglied erhält dann keinerlei Punkte mehr.`) && save(allOn, "Komplett gesperrt")} disabled={busy} data-testid="member-excl-all" className="w-full h-12 rounded-full bg-rose-600 text-white font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><Ban className="w-4 h-4" /> Alles sperren</button>
        )}
        <p className="text-xs text-slate-400">Das Mitglied sieht jeweils einen Hinweis. Alle Ausschlüsse findest du auch im Admin-Bereich unter „Ausschlüsse & Verdacht“.</p>
      </div>
    </div>
  );
}
